// Every widget the dashboard can hold, keyed by the code the database's widget
// catalogue uses. A widget reads only through routes that already check
// permissions, follows the live topics behind its data, and draws a skeleton
// first, an explanation when there is nothing, and a short error it can retry.
//
// Each widget leads with one large figure or one picture; anything else it says
// is small, beside or under it, so the page reads from the big things down.

import React, { useId, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Area, AreaChart, Bar, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, BadgeCheck, BellRing, Boxes, CircleDollarSign, ClipboardList, Clock, FileText, FlaskConical, FolderOpen, Globe2,
  History, Inbox, LayoutGrid, MapPin, Megaphone, Monitor, MousePointerClick, Package, PackageCheck, PackagePlus, Radio, ScanLine, Server,
  ShieldCheck, ShoppingBag, ShoppingCart, Smartphone, Sparkles, TrendingUp, Truck, Upload, Users, Warehouse, Workflow, Zap,
} from 'lucide-react';

import { tx, hasTx } from './i18n';
import { auditActionLabel, notificationEventMessage, notificationEventTitle } from './labels';
import { destinationsOf, visibleNav } from './nav';
import { useXomicsAuth } from './AuthContext';
import { countryName } from './countries';
import { CHART, ChartTip, Flag, PulseMap, tipDay } from './MobileParts';
import { activatorName, kitName, placeLabel, stepLabel } from './CollectionActivity';
import {
  AUDITED_GAP_MS, COMPACT, MINUTE, NUMBER, PERCENT, activationsReader, activityReader, ago, counted, daily, money,
  ordersReader, readAnalyses, readAudit, readJobs, readLabApprovals, readLabSamples, readLabWork, readMobile, readNotifications,
  readOverview, readPipelines, readRecents, readReportAnalytics, readReportQueue, readRequestedUploads, readRevenue, readStoreCampaigns, readStoreLive,
  readStoreMonth, readStoreWeek, scoped, shipmentsReader, t, tilePixels, useWidgetData, validationsReader, week,
} from './DashboardData';
import {
  ChartKey, Counter, Delta, EmptyNote, Facts, Headline, KpiCard, KpiTile, Loaded, Ring, Row, RowList, ShareBar, SkeletonChart,
  SkeletonKpi, SkeletonList, SkeletonMap, StageBar, Tag, Tally, WidgetCard,
} from './DashboardParts';

const w = (code, key, values) => t(`widget.${code}.${key}`, values);
const title = (code) => w(code, 'title');
// How many list rows a tile `height` grid rows tall has room for, after `used` pixels
// taken by its padding, its header and anything drawn above the list.
const LIST_ROW = 56;
const rowsFor = (height, used) => Math.max(1, Math.floor((tilePixels(height) - used) / LIST_ROW));
// What a list's header and padding take, with a split bar, or with a tally above it.
const LIST = 140;
const WITH_BAR = 200;
const WITH_TALLY = 260;
// Portal activity: its figures and hourly bars, then one line per busiest action.
const AUDIT_ABOVE = 300;
const AUDIT_ROW = 36;

// A label the catalogue may or may not have: the section's own word, else the code.
function known(prefix, code) {
  const key = `${prefix}${String(code || '').toLowerCase()}`;
  return hasTx(key) ? tx(key) : String(code || '');
}
const storeText = (group, code) => known(`ui.storefront.${group}.`, code);

function duration(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return t('none');
  if (ms < 1000) return t('units.ms', { value: Math.round(ms) });
  const seconds = ms / 1000;
  if (seconds < 90) return t('units.seconds', { value: NUMBER.format(Math.round(seconds)) });
  const minutes = seconds / 60;
  if (minutes < 90) return t('units.minutes', { value: NUMBER.format(Math.round(minutes)) });
  return t('units.hours', { value: new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(minutes / 60) });
}

const dayLabel = (at) => new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const tip = (at) => tipDay(at);
const AXIS = { tickLine: false, axisLine: false, tick: CHART.tick };
// Room on the right for the last day's label, and on the left for the axis's.
const MARGIN = { top: 8, right: 12, left: 4, bottom: 0 };
const INK = '#1f3b8f';
const WARM = '#d9957f';

// ------------------------------------------------------------------ headline numbers

function KpiShell({ code, hint, hintTitle, to, resource, children }) {
  return (
    <KpiCard title={title(code)} hint={hint} hintTitle={hintTitle} to={to}>
      <Loaded resource={resource} skeleton={<SkeletonKpi />}>{children}</Loaded>
    </KpiCard>
  );
}

// The first day's date starts at the chart's left edge and the last ends at its
// right, so neither is cut.
function EdgeTick({ x, y, payload, first }) {
  return (
    <text x={x} y={y} dominantBaseline="hanging" textAnchor={payload.value === first ? 'start' : 'end'} className="xo-dash-kpi-tick">{dayLabel(payload.value)}</text>
  );
}

// A headline measure's month: an area over its days with the first and last dated
// under it, and the last seven shaded, since the figure above counts those. Hovering
// names each day and its value. A rate is drawn between its own lowest and highest
// point, so a change of a fraction of a percent still shows.
function KpiChart({ points, name, format = (value) => NUMBER.format(value), fromZero = true }) {
  const id = useId().replace(/:/g, '');
  if (points.length < 2) return null;
  const first = points[0].at;
  const last = points[points.length - 1].at;
  return (
    <div className="xo-dash-kpi-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 6, right: 2, left: 2, bottom: 0 }}>
          <defs>
            <linearGradient id={`kpi-${id}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={INK} stopOpacity={0.2} /><stop offset="100%" stopColor={INK} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="at" type="number" domain={[first, last]} ticks={[first, last]} interval={0} height={18} tickSize={0} tickMargin={4}
            tickLine={false} axisLine={false} tick={<EdgeTick first={first} />} />
          <YAxis hide domain={fromZero ? [0, 'auto'] : ['dataMin', 'dataMax']} />
          <ReferenceArea x1={points[Math.max(0, points.length - 7)].at} x2={last} fill={INK} fillOpacity={0.05} strokeOpacity={0} />
          <Tooltip cursor={CHART.lineCursor} animationDuration={120} content={(
            <ChartTip title={tip} rows={(point) => [{ name, color: INK, mark: 'line', text: format(point.value) }]} />
          )} />
          <Area dataKey="value" type="monotone" stroke={INK} strokeWidth={1.75} fill={`url(#kpi-${id})`} isAnimationActive={false}
            activeDot={{ r: 3.5, fill: INK, stroke: '#fff', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// The last seven days against the seven before, with the month as a chart.
function WeekTile({ points, name, format, tipFormat = format, goodWhen = 'up' }) {
  const span = week(points.map((point) => point.value));
  return (
    <KpiTile value={span.current} format={format} current={span.current} previous={span.previous}
      goodWhen={goodWhen} comparison={t('kpi.vs_previous_week')} chart={<KpiChart points={points} name={name} format={tipFormat} />} />
  );
}

function KpiRevenue() {
  const resource = useWidgetData(['orders'], 'revenue', readRevenue);
  const data = resource.data;
  const hint = data && data.analytics ? data.analytics.currency.code : null;
  const others = data && data.currencies.length > 1
    ? t('kpi_revenue.others', { currency: data.currencies[0].currency, others: data.currencies.slice(1).map((entry) => entry.currency).join(', ') })
    : null;
  return (
    <KpiShell code="kpi_revenue" hint={hint} hintTitle={others} to="/xomics/commerce/overview" resource={resource}>
      {(loaded) => {
        if (loaded.analytics === null) return <EmptyNote title={t('kpi_revenue.empty')} detail={t('kpi_revenue.empty_detail')} />;
        const { currency } = loaded.analytics;
        return (
          <WeekTile points={daily(loaded.analytics.daily, 30, (point) => point.netCollectedMinor)} name={t('revenue.net')}
            format={(value) => money(value, currency.code, currency.minorUnit, { compact: true })}
            tipFormat={(value) => money(value, currency.code, currency.minorUnit)} />
        );
      }}
    </KpiShell>
  );
}

function KpiUnsentOrders({ permissions }) {
  const scope = scoped(permissions).orders;
  const load = useMemo(() => ordersReader(scope), [scope]);
  const resource = useWidgetData(['orders', 'shipments'], `orders-${scope}`, load);
  return (
    <KpiShell code="kpi_unsent_orders" hint={t(scope === 'global' ? 'scope.everywhere' : 'scope.local')} to="/xomics/orders" resource={resource}>
      {(data) => {
        const oldest = data.unsent.reduce((min, order) => Math.min(min, Number(order.placedAt) || Infinity), Infinity);
        const placed = daily(data.placed.map((order) => ({ at: Number(order.placedAt) })), 30, () => 1);
        return (
          <KpiTile value={data.unsentTotal} tone={data.unsentTotal > 0 ? 'attention' : null}
            detail={Number.isFinite(oldest) ? t('kpi_orders.oldest', { when: ago(oldest) }) : counted('kpi_orders.placed', data.placed.length)}
            chart={<KpiChart points={placed} name={t('store.orders')} />} />
        );
      }}
    </KpiShell>
  );
}

function KpiActivations({ permissions }) {
  const scope = scoped(permissions).kits;
  const load = useMemo(() => activityReader(scope), [scope]);
  const resource = useWidgetData(['collections'], `activity-${scope}`, load, { reconcileMs: MINUTE });
  return (
    <KpiShell code="kpi_activations" hint={t('kpi.last_7_days')} hintTitle={t(scope === 'global' ? 'scope.everywhere' : 'scope.local')} to="/xomics/kits"
      resource={resource}>
      {(data) => <WeekTile points={data.series.map((point) => ({ at: point.at, value: point.app + point.portal + point.unrecorded }))} name={t('activations.total')} />}
    </KpiShell>
  );
}

function KpiValidations({ permissions }) {
  const global = scoped(permissions).validationsGlobal;
  const load = useMemo(() => validationsReader(global), [global]);
  const resource = useWidgetData(['validations'], `validations-${global}`, load);
  return (
    <KpiShell code="kpi_validations" hint={t('scope.local')} to="/xomics/validation" resource={resource}>
      {(data) => {
        const oldest = data.local.validations.reduce((min, item) => Math.min(min, Number(item.createdAt) || Infinity), Infinity);
        return (
          <KpiTile value={data.local.total} tone={data.local.total > 0 ? 'attention' : null}
            detail={Number.isFinite(oldest) ? t('kpi_validations.oldest', { when: ago(oldest) }) : t('kpi_validations.clear')}
            stages={data.global ? [
              { key: 'local', tone: 'navy', label: t('scope.local'), value: data.local.total },
              { key: 'elsewhere', tone: 'soft', label: t('kpi_validations.elsewhere'), value: Math.max(0, data.global.total - data.local.total) },
            ] : null} />
        );
      }}
    </KpiShell>
  );
}

const LAB_WAITING = ['awaiting_acceptance', 'ready'];

// The laboratory's figures belong to the laboratory an account works at; anyone
// else sees why there are none instead of an error.
const atLab = (render) => (data) => (data.notLaboratory
  ? <EmptyNote icon={FlaskConical} title={t('lab.not_at_lab')} detail={t('lab.not_at_lab_detail')} />
  : render(data));

function KpiLabQueue() {
  const resource = useWidgetData(['samples', 'laboratory'], 'lab-samples', readLabSamples);
  return (
    <KpiShell code="kpi_lab_queue" to="/xomics/laboratory/samples" resource={resource}>
      {atLab((data) => {
        const counts = data.statusCounts;
        const waiting = LAB_WAITING.reduce((sum, key) => sum + (counts[key] || 0), 0);
        const held = (counts.on_hold || 0) + (counts.quarantined || 0);
        return (
          <KpiTile value={waiting} tone={held > 0 ? 'attention' : null}
            detail={held > 0 ? counted('kpi_lab.held', held) : counted('kpi_lab.ready', counts.ready || 0)}
            stages={[['awaiting_acceptance', 'mid'], ['ready', 'navy'], ['on_hold', 'warn'], ['quarantined', 'bad']].map(([key, tone]) => ({
              key, tone, label: tx(`ui.lab.inventory.tab.${key}`), value: counts[key] || 0,
            }))} />
        );
      })}
    </KpiShell>
  );
}

function KpiLabWork() {
  const resource = useWidgetData(['samples', 'laboratory'], 'lab-work', readLabWork);
  return (
    <KpiShell code="kpi_lab_work" to="/xomics/laboratory/work" resource={resource}>
      {atLab((data) => {
        const counts = data.statusCounts;
        return (
          <KpiTile value={(counts.planned || 0) + (counts.in_progress || 0)}
            detail={counted('kpi_lab.awaiting_approval', counts.awaiting_approval || 0)} tone={(counts.awaiting_approval || 0) > 0 ? 'attention' : null}
            stages={[['planned', 'soft'], ['in_progress', 'navy'], ['awaiting_approval', 'warn'], ['awaiting_customer', 'warn']].map(([key, tone]) => ({
              key, tone, label: tx(`ui.lab.stage.order.${key}`), value: counts[key] || 0,
            }))} />
        );
      })}
    </KpiShell>
  );
}

function KpiReports() {
  const resource = useWidgetData(['reports'], 'report-analytics', readReportAnalytics, { gapMs: AUDITED_GAP_MS });
  return (
    <KpiShell code="kpi_reports" hint={t('kpi.last_7_days')} to="/xomics/reporting/analytics" resource={resource}>
      {(data) => <WeekTile points={data.series.map((point) => ({ at: point.at, value: point.reports }))} name={t('reports.requested')} />}
    </KpiShell>
  );
}

function KpiAnalyses() {
  const resource = useWidgetData(['jobs'], 'analyses', readAnalyses, { gapMs: AUDITED_GAP_MS });
  const scope = resource.data ? t(resource.data.scope === 'mine' ? 'scope.yours' : 'scope.everyone') : null;
  return (
    <KpiShell code="kpi_analyses" hint={t('kpi.last_7_days')} hintTitle={scope} to="/xomics/uploads" resource={resource}>
      {(data) => <WeekTile points={daily(data.series, 30, (point) => point.submitted)} name={t('analyses.submitted')} />}
    </KpiShell>
  );
}

// The store's own figures come with the period before already counted.
// `pick` may name its own format (money, in the currency it found); `trendOf` gives
// the month as dated points.
function StoreKpi({ code, pick, format, goodWhen = 'up', trendOf, rate = false }) {
  const current = useWidgetData(['store_traffic', 'store'], 'store-week', readStoreWeek, { gapMs: MINUTE });
  const month = useWidgetData(['store_traffic', 'store'], 'store-month', readStoreMonth, { gapMs: MINUTE });
  const site = current.data && current.data.site ? current.data.site.name : null;
  return (
    <KpiShell code={code} hint={t('kpi.last_7_days')} hintTitle={site} to="/xomics/store/overview" resource={current}>
      {(data) => {
        if (data.report === null) return <EmptyNote title={t('store.no_site')} detail={t('store.no_site_detail')} />;
        const figure = pick(data.report.kpis);
        if (figure === null) return <EmptyNote title={t('store.no_sales')} detail={t('store.no_sales_detail')} />;
        const shown = figure.format || format;
        const points = month.data && month.data.report ? trendOf(month.data.report) : null;
        return (
          <KpiTile value={figure.value} format={shown} current={figure.value} previous={figure.previous}
            goodWhen={goodWhen} comparison={t('kpi.vs_previous_week')}
            chart={points && <KpiChart points={points} name={title(code)} format={figure.tipFormat || shown} fromZero={!rate} />} />
        );
      }}
    </KpiShell>
  );
}

function KpiStoreVisitors() {
  return <StoreKpi code="kpi_store_visitors" pick={(kpis) => kpis.visitors}
    trendOf={(report) => report.series.map((point) => ({ at: point.at, value: point.visitors }))} />;
}

function KpiStoreConversion() {
  return <StoreKpi code="kpi_store_conversion" format={(value) => PERCENT.format(value)} rate
    pick={(kpis) => (kpis.conversionRate.value === null ? null : kpis.conversionRate)}
    trendOf={(report) => report.series.map((point) => ({ at: point.at, value: point.sessions > 0 ? point.orders / point.sessions : 0 }))} />;
}

function KpiStoreRevenue() {
  return <StoreKpi code="kpi_store_revenue"
    pick={(kpis) => {
      const main = kpis.revenue.value[0];
      if (!main) return null;
      const before = kpis.revenue.previous.find((entry) => entry.currency === main.currency);
      return {
        value: main.netMinor, previous: before ? before.netMinor : 0,
        format: (value) => money(value, main.currency, main.minorUnit, { compact: true }),
        tipFormat: (value) => money(value, main.currency, main.minorUnit),
      };
    }}
    trendOf={(report) => {
      const code = report.kpis.revenue.value[0] ? report.kpis.revenue.value[0].currency : null;
      return report.revenueSeries.map((point) => ({ at: point.at, value: (point.revenue.find((entry) => entry.currency === code) || { netMinor: 0 }).netMinor }));
    }} />;
}

function KpiAppPeople() {
  const resource = useWidgetData(['mobile'], 'mobile', readMobile, { gapMs: MINUTE, reconcileMs: MINUTE });
  return (
    <KpiShell code="kpi_app_people" hint={t('kpi.last_7_days')} to="/xomics/mobile/overview" resource={resource}>
      {(data) => (
        <KpiTile value={data.activity.opened.week} detail={t('kpi_app.today', { count: NUMBER.format(data.activity.opened.day) })}
          chart={<KpiChart points={data.sessions.series.map((point) => ({ at: point.at, value: point.people }))} name={t('app.people')} />} />
      )}
    </KpiShell>
  );
}

// ------------------------------------------------------------------ charts

// The chart takes whatever height its tile leaves it (dashboard.css keeps a floor).
function ChartFrame({ children }) {
  return <div className="xo-dash-chart">{children}</div>;
}

function ChartRevenue({ width }) {
  const resource = useWidgetData(['orders'], 'revenue', readRevenue);
  return (
    <WidgetCard title={title('chart_revenue')} detail={w('chart_revenue', 'detail')} to="/xomics/commerce/analytics">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.analytics === null) return <EmptyNote icon={CircleDollarSign} title={t('kpi_revenue.empty')} detail={t('kpi_revenue.empty_detail')} />;
          const { currency, totals } = data.analytics;
          const fmt = (value) => money(value, currency.code, currency.minorUnit, { compact: true });
          // Bars are what was collected each day (never below zero, so the payments line shares their floor);
          // refunds are named in the hover and beside the headline.
          const collected = daily(data.analytics.daily, 30, (point) => point.collectedMinor);
          const refunded = daily(data.analytics.daily, 30, (point) => point.refundedMinor);
          const paid = daily(data.analytics.daily, 30, (point) => point.succeeded);
          const points = collected.map((point, index) => ({ at: point.at, collected: point.value, refunded: refunded[index].value, paid: paid[index].value }));
          return (
            <>
              <Headline value={totals.netCollectedMinor} format={fmt} caption={t('revenue.net')} side={[
                { label: t('revenue.paid_orders'), value: totals.paidOrders },
                { label: t('revenue.average'), value: totals.averageOrderMinor, format: fmt },
                width >= 3 ? { label: t('revenue.refunded'), value: totals.refundedMinor, format: fmt } : null,
              ]} />
              <ChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={points} margin={MARGIN}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
                    <YAxis yAxisId="money" tickFormatter={(value) => money(value, currency.code, currency.minorUnit, { compact: true, whole: true })}
                      width={62} domain={[0, 'auto']} {...AXIS} />
                    <YAxis yAxisId="count" orientation="right" hide allowDecimals={false} />
                    <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                      <ChartTip title={tip} rows={(point) => [
                        { name: t('revenue.collected'), color: INK, mark: 'bar', text: money(point.collected, currency.code, currency.minorUnit) },
                        point.refunded > 0 ? { name: t('revenue.refunded'), color: CHART.soft, mark: 'bar', text: money(point.refunded, currency.code, currency.minorUnit) } : null,
                        { name: t('revenue.payments'), color: WARM, mark: 'line', text: NUMBER.format(point.paid) },
                      ]} />
                    )} />
                    <Bar yAxisId="money" dataKey="collected" fill={INK} radius={[3, 3, 0, 0]} maxBarSize={16} />
                    <Line yAxisId="count" dataKey="paid" type="monotone" stroke={WARM} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartFrame>
              <ChartKey items={[{ label: t('revenue.collected_in', { currency: currency.code }), color: INK }, { label: t('revenue.payments'), color: WARM, line: true }]} />
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function ChannelSplit({ channels }) {
  const total = channels.reduce((sum, channel) => sum + channel.sessions, 0);
  const shades = [CHART.deep, INK, '#435ba1', CHART.mid, CHART.soft, '#cfd6ea'];
  const sorted = [...channels].sort((a, b) => b.sessions - a.sessions);
  if (total === 0) return null;
  return (
    <div className="xo-dash-split">
      <div className="xo-dash-split-bar">{sorted.map((channel, index) => (
        <i key={channel.channel} style={{ flexGrow: channel.sessions, background: shades[index % shades.length] }} title={storeText('channel', channel.channel)} />
      ))}</div>
      <div className="xo-dash-key">{sorted.slice(0, 5).map((channel, index) => (
        <span key={channel.channel}><i style={{ background: shades[index % shades.length] }} />{storeText('channel', channel.channel)}<b>{PERCENT.format(channel.sessions / total)}</b></span>
      ))}</div>
    </div>
  );
}

function ChartStoreTraffic() {
  const resource = useWidgetData(['store_traffic', 'store'], 'store-month', readStoreMonth, { gapMs: MINUTE });
  return (
    <WidgetCard title={title('chart_store_traffic')} detail={w('chart_store_traffic', 'detail')} to="/xomics/store/overview">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.report === null) return <EmptyNote icon={Globe2} title={t('store.no_site')} detail={t('store.no_site_detail')} />;
          const { kpis, series, channels } = data.report;
          return (
            <>
              <Headline value={kpis.visitors.value} caption={t('store.visitors')}
                delta={<Delta current={kpis.visitors.value} previous={kpis.visitors.previous} label={t('kpi.vs_previous_week')} />}
                side={[
                  { label: t('store.orders'), value: kpis.orders.value },
                  { label: t('store.conversion'), value: kpis.conversionRate.value === null ? t('none') : PERCENT.format(kpis.conversionRate.value) },
                ]} />
              <ChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={series} margin={MARGIN}>
                    <defs>
                      <linearGradient id="dash-sessions" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={INK} stopOpacity={0.18} /><stop offset="100%" stopColor={INK} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
                    <YAxis yAxisId="sessions" width={40} tickFormatter={(value) => COMPACT.format(value)} {...AXIS} />
                    <YAxis yAxisId="orders" orientation="right" hide allowDecimals={false} domain={[0, (most) => Math.max(1, most * 2.6)]} />
                    <Tooltip cursor={CHART.lineCursor} animationDuration={120} content={(
                      <ChartTip title={tip} rows={(point) => [
                        { name: t('store.sessions'), color: INK, mark: 'line', text: NUMBER.format(point.sessions) },
                        { name: t('store.visitors'), color: CHART.mid, mark: 'line', text: NUMBER.format(point.visitors) },
                        { name: t('store.orders'), color: WARM, mark: 'bar', text: NUMBER.format(point.orders) },
                      ]} />
                    )} />
                    <Bar yAxisId="orders" dataKey="orders" fill={WARM} radius={[3, 3, 0, 0]} maxBarSize={10} opacity={0.85} />
                    <Area yAxisId="sessions" dataKey="sessions" type="monotone" stroke={INK} strokeWidth={2} fill="url(#dash-sessions)" />
                    <Line yAxisId="sessions" dataKey="visitors" type="monotone" stroke={CHART.mid} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartFrame>
              <ChannelSplit channels={channels} />
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function ChartAnalyses() {
  const resource = useWidgetData(['jobs'], 'analyses', readAnalyses, { gapMs: AUDITED_GAP_MS });
  return (
    <WidgetCard title={title('chart_analyses')} to="/xomics/uploads"
      detail={resource.data ? w('chart_analyses', resource.data.scope === 'mine' ? 'detail_mine' : 'detail') : null}>
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.totals.runs === 0) return <EmptyNote icon={Activity} title={t('analyses.empty')} detail={t('analyses.empty_detail')} />;
          const complete = daily(data.series, 30, (point) => point.complete);
          const failed = daily(data.series, 30, (point) => point.failed);
          const submitted = daily(data.series, 30, (point) => point.submitted);
          const points = complete.map((point, index) => ({ at: point.at, complete: point.value, failed: failed[index].value, submitted: submitted[index].value }));
          const top = data.pipelines.slice(0, 4);
          const most = Math.max(1, ...top.map((pipeline) => pipeline.runs));
          return (
            <div className="xo-dash-chart-split">
              <div>
                <Headline value={data.totals.runs} caption={t('analyses.runs')} side={[
                  { label: t('analyses.failed'), value: data.totals.failed, tone: data.totals.failed > 0 ? 'bad' : null },
                  { label: t('analyses.average'), value: duration(data.totals.avgRunMs) },
                ]} />
                <ChartFrame>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={points} margin={MARGIN}>
                      <CartesianGrid vertical={false} stroke={CHART.grid} />
                      <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
                      <YAxis allowDecimals={false} width={30} {...AXIS} />
                      <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                        <ChartTip title={tip} rows={(point) => [
                          { name: t('analyses.complete'), color: INK, mark: 'bar', text: NUMBER.format(point.complete) },
                          { name: t('analyses.failed'), color: WARM, mark: 'bar', text: NUMBER.format(point.failed) },
                          { name: t('analyses.submitted'), color: CHART.mid, mark: 'line', text: NUMBER.format(point.submitted) },
                        ]} />
                      )} />
                      <Bar dataKey="complete" stackId="runs" fill={INK} maxBarSize={14} />
                      <Bar dataKey="failed" stackId="runs" fill={WARM} radius={[3, 3, 0, 0]} maxBarSize={14} />
                      <Line dataKey="submitted" type="monotone" stroke={CHART.mid} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartFrame>
                <ChartKey items={[{ label: t('analyses.complete'), color: INK }, { label: t('analyses.failed'), color: WARM },
                  { label: t('analyses.submitted'), color: CHART.mid, line: true }]} />
              </div>
              {top.length > 0 && (
                <ol className="xo-dash-rank is-aside">
                  <li className="xo-dash-rank-head">{t('analyses.by_pipeline')}</li>
                  {top.map((pipeline) => (
                    <li key={pipeline.key}>
                      <span><strong>{pipeline.label}</strong><small>{counted('analyses.failed_count', pipeline.failed)} · {duration(pipeline.avgRunMs)}</small></span>
                      <ShareBar value={pipeline.runs} total={most} />
                      <em>{NUMBER.format(pipeline.runs)}</em>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function ChartActivations({ permissions }) {
  const scope = scoped(permissions).kits;
  const load = useMemo(() => activityReader(scope), [scope]);
  const resource = useWidgetData(['collections'], `activity-${scope}`, load, { reconcileMs: MINUTE });
  return (
    <WidgetCard title={title('chart_activations')} detail={w('chart_activations', 'detail')} to="/xomics/kits">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.totals.activations === 0) return <EmptyNote icon={Package} title={t('activations.empty')} detail={t('activations.empty_detail')} />;
          return (
            <>
              <Headline value={data.totals.activations} caption={t('activations.total')} side={[
                { label: t('activations.app'), value: data.totals.app },
                { label: t('activations.portal'), value: data.totals.portal },
                { label: t('activations.countries'), value: data.totals.countries },
              ]} />
              <ChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.series} margin={MARGIN}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
                    <YAxis allowDecimals={false} width={30} {...AXIS} />
                    <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                      <ChartTip title={tip} rows={(point) => [
                        { name: t('activations.app'), color: INK, mark: 'bar', text: NUMBER.format(point.app) },
                        { name: t('activations.portal'), color: CHART.mid, mark: 'bar', text: NUMBER.format(point.portal) },
                        point.unrecorded > 0 ? { name: t('activations.unrecorded'), color: CHART.soft, mark: 'bar', text: NUMBER.format(point.unrecorded) } : null,
                      ]} />
                    )} />
                    <Bar dataKey="app" stackId="a" fill={INK} maxBarSize={14} />
                    <Bar dataKey="portal" stackId="a" fill={CHART.mid} maxBarSize={14} />
                    <Bar dataKey="unrecorded" stackId="a" fill={CHART.soft} radius={[3, 3, 0, 0]} maxBarSize={14} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartFrame>
              <ChartKey items={[{ label: t('activations.app'), color: INK }, { label: t('activations.portal'), color: CHART.mid }]} />
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function ChartReports() {
  const resource = useWidgetData(['reports'], 'report-analytics', readReportAnalytics, { gapMs: AUDITED_GAP_MS });
  return (
    <WidgetCard title={title('chart_reports')} detail={w('chart_reports', 'detail')} to="/xomics/reporting/analytics">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.totals.reports === 0) return <EmptyNote icon={FileText} title={t('reports.empty')} detail={t('reports.empty_detail')} />;
          const points = data.series.map((point) => ({
            at: point.at, done: point.byStatus.done || 0, failed: point.byStatus.failed || 0,
            active: (point.byStatus.queued || 0) + (point.byStatus.processing || 0),
          }));
          return (
            <>
              <Headline value={data.totals.reports} caption={t('reports.requested')} side={[
                { label: t('reports.failure_share'), value: data.totals.failureShare === null ? t('none') : PERCENT.format(data.totals.failureShare),
                  tone: data.totals.failureShare > 0.05 ? 'bad' : null },
                { label: t('reports.median_time'), value: duration(data.totals.medianGenerationMs) },
              ]} />
              <ChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={points} margin={MARGIN}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
                    <YAxis allowDecimals={false} width={30} {...AXIS} />
                    <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                      <ChartTip title={tip} rows={(point) => [
                        { name: t('reports.done'), color: INK, mark: 'bar', text: NUMBER.format(point.done) },
                        point.failed > 0 ? { name: t('reports.failed'), color: WARM, mark: 'bar', text: NUMBER.format(point.failed) } : null,
                        point.active > 0 ? { name: t('reports.active'), color: CHART.soft, mark: 'bar', text: NUMBER.format(point.active) } : null,
                      ]} />
                    )} />
                    <Bar dataKey="done" stackId="r" fill={INK} maxBarSize={14} />
                    <Bar dataKey="failed" stackId="r" fill={WARM} maxBarSize={14} />
                    <Bar dataKey="active" stackId="r" fill={CHART.soft} radius={[3, 3, 0, 0]} maxBarSize={14} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartFrame>
              <ChartKey items={[{ label: t('reports.done'), color: INK }, { label: t('reports.failed'), color: WARM }, { label: t('reports.active'), color: CHART.soft }]} />
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function ChartLabIntake() {
  const resource = useWidgetData(['samples', 'laboratory'], 'lab-samples', readLabSamples);
  return (
    <WidgetCard title={title('chart_lab_intake')} detail={w('chart_lab_intake', 'detail')} to="/xomics/laboratory/receipt">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {atLab((data) => {
          const received = data.samples.filter((sample) => Number(sample.lastReceivedAt) > 0);
          if (received.length === 0) return <EmptyNote icon={ScanLine} title={t('lab.no_intake')} detail={t('lab.no_intake_detail')} />;
          const points = daily(received.map((sample) => ({ at: Number(sample.lastReceivedAt) })), 14, () => 1);
          const counts = data.statusCounts;
          return (
            <>
              <Headline value={points.reduce((sum, point) => sum + point.value, 0)} caption={t('lab.received_14')} side={[
                { label: t('lab.in_inventory'), value: counts.all || 0 },
                { label: tx('ui.lab.inventory.tab.ready'), value: counts.ready || 0 },
              ]} />
              <ChartFrame>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={points} margin={MARGIN}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={20} {...AXIS} />
                    <YAxis allowDecimals={false} width={30} {...AXIS} />
                    <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                      <ChartTip title={tip} rows={(point) => [{ name: t('lab.received'), color: INK, mark: 'bar', text: NUMBER.format(point.value) }]} />
                    )} />
                    <Bar dataKey="value" fill={INK} radius={[3, 3, 0, 0]} maxBarSize={24} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartFrame>
              {data.total > data.samples.length && <p className="xo-dash-note">{t('lab.intake_partial', { count: NUMBER.format(data.samples.length) })}</p>}
            </>
          );
        })}
      </Loaded>
    </WidgetCard>
  );
}

// ------------------------------------------------------------------ maps

// The map, with its figures beside it once the card is wide enough (dashboard.css decides, by the card's own width).
function MapLayout({ map, side }) {
  return <div className="xo-dash-map-layout"><div className="xo-dash-map-art">{map}</div><div className="xo-dash-map-side">{side}</div></div>;
}


// The large figure a map is about.
function MapCount({ value, caption, live = null }) {
  return (
    <div className="xo-dash-live-count">
      <strong>{live !== null && <span className={`xo-dash-pulse${live ? ' is-on' : ''}`} aria-hidden="true" />}<Counter value={value} /></strong>
      <small>{caption}</small>
    </div>
  );
}

function PlaceList({ rows, unit }) {
  const most = Math.max(1, ...rows.map((row) => row.value));
  return (
    <ol className="xo-dash-places">
      {rows.map((row) => (
        <li key={row.key}>
          <Flag code={row.code} />
          <span><strong>{row.name}</strong>{row.detail && <small>{row.detail}</small>}</span>
          <ShareBar value={row.value} total={most} />
          <em title={unit}>{row.text || NUMBER.format(row.value)}</em>
        </li>
      ))}
    </ol>
  );
}

function MapStoreLive() {
  const resource = useWidgetData(['store_live'], 'store-live', readStoreLive, { gapMs: 5000, reconcileMs: 30000 });
  return (
    <WidgetCard title={title('map_store_live')} detail={w('map_store_live', 'detail')} to="/xomics/store/live" className="is-map">
      <Loaded resource={resource} skeleton={<SkeletonMap />}>
        {(data) => {
          if (data.live === null) return <EmptyNote icon={Radio} title={t('store.no_site')} detail={t('store.no_site_detail')} />;
          const { live } = data;
          const countries = new Map();
          for (const place of live.places) countries.set(place.country, (countries.get(place.country) || 0) + place.visitors);
          const pings = live.places.filter((place) => Number.isFinite(place.lat) && Number.isFinite(place.lng))
            .map((place) => ({ city: place.city, country: place.country, lat: place.lat, lng: place.lng, at: live.generatedAt, count: place.visitors }));
          const states = [
            { key: 'browsing', value: live.totals.browsing, tone: 'soft' },
            { key: 'with_bag', value: live.totals.withBag, tone: 'mid' },
            { key: 'in_checkout', value: live.totals.inCheckout, tone: 'navy' },
            { key: 'purchased', value: live.totals.purchased, tone: 'good' },
          ].map((state) => ({ ...state, label: t(`store.state_${state.key}`) }));
          return (
            <MapLayout
              map={<PulseMap countries={[...countries.entries()].map(([code, visitors]) => ({ code, visitors }))} valueKey="visitors" fit
                valueLabel={t('store.visitors')} pings={pings} pingCountKey="ui.dash.store.visitors_count" />}
              side={(
                <>
                  <MapCount value={live.visitors} caption={t('store.in_store_now')} live={live.visitors > 0} />
                  <StageBar stages={states} compact />
                  {live.pages.length > 0 && (
                    <div className="xo-dash-live-pages">
                      <small>{t('store.top_pages_now')}</small>
                      {live.pages.slice(0, 3).map((page) => <span key={page.path}><code>{page.path}</code><b>{NUMBER.format(page.visitors)}</b></span>)}
                    </div>
                  )}
                </>
              )} />
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function MapActivations({ permissions }) {
  const scope = scoped(permissions).kits;
  const load = useMemo(() => activityReader(scope), [scope]);
  const resource = useWidgetData(['collections'], `activity-${scope}`, load, { reconcileMs: MINUTE });
  return (
    <WidgetCard title={title('map_activations')} detail={w('map_activations', 'detail')} to="/xomics/kits" className="is-map">
      <Loaded resource={resource} skeleton={<SkeletonMap />}>
        {(data) => {
          if (data.countries.length === 0) return <EmptyNote icon={MapPin} title={t('activations.no_places')} detail={t('activations.no_places_detail')} />;
          const rows = data.cities.slice(0, 4).map((city) => ({
            key: `${city.city}|${city.countryCode}`, code: city.countryCode, name: city.city || countryName(city.countryCode),
            detail: `${city.countryCode ? countryName(city.countryCode) : t('none')} · ${ago(city.latestAt)}`, value: city.activations,
          }));
          return (
            <MapLayout
              map={<PulseMap countries={data.countries} valueKey="activations" valueLabel={t('activations.total')} />}
              side={(
                <>
                  <MapCount value={data.totals.activations} caption={counted('activations.in_countries', data.totals.countries)} />
                  <PlaceList rows={rows} unit={t('activations.total')} />
                </>
              )} />
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function MapApp() {
  const resource = useWidgetData(['mobile'], 'mobile', readMobile, { gapMs: MINUTE, reconcileMs: MINUTE });
  return (
    <WidgetCard title={title('map_app')} detail={w('map_app', 'detail')} to="/xomics/mobile/overview" className="is-map">
      <Loaded resource={resource} skeleton={<SkeletonMap />}>
        {(data) => {
          const rows = [...data.geography.cities].filter((city) => city.accounts > 0).sort((a, b) => b.accounts - a.accounts).slice(0, 4)
            .map((city) => ({ key: `${city.city}|${city.country}`, code: city.country, name: city.city, detail: countryName(city.country), value: city.accounts }));
          return (
            <MapLayout
              map={<PulseMap countries={data.geography.countries} pings={data.pings} fit />}
              side={(
                <>
                  <MapCount value={data.activity.inApp.people.length} caption={t('app.in_app_now')} live={data.activity.inApp.people.length > 0} />
                  {rows.length > 0 ? <PlaceList rows={rows} unit={t('app.people')} /> : <p className="xo-dash-note">{t('app.no_places')}</p>}
                </>
              )} />
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function MapOrders() {
  const resource = useWidgetData(['orders'], 'revenue', readRevenue);
  return (
    <WidgetCard title={title('map_orders')} detail={w('map_orders', 'detail')} to="/xomics/commerce/analytics" className="is-map">
      <Loaded resource={resource} skeleton={<SkeletonMap />}>
        {(data) => {
          if (data.analytics === null || data.analytics.markets.length === 0) return <EmptyNote icon={Globe2} title={t('kpi_revenue.empty')} detail={t('kpi_revenue.empty_detail')} />;
          const { currency, markets } = data.analytics;
          const rows = [...markets].sort((a, b) => b.orderCount - a.orderCount).slice(0, 4).map((market) => ({
            key: market.countryCode, code: market.countryCode, name: countryName(market.countryCode), value: market.orderCount,
            detail: `${counted('revenue.orders_count', market.orderCount)} · ${money(market.netCollectedMinor, currency.code, currency.minorUnit, { compact: true })}`,
          }));
          return (
            <MapLayout
              map={<PulseMap countries={markets.map((market) => ({ code: market.countryCode, orders: market.orderCount }))} valueKey="orders"
                valueLabel={t('store.orders')} />}
              side={(
                <>
                  <MapCount value={markets.length} caption={t('revenue.markets')} />
                  <PlaceList rows={rows} unit={t('store.orders')} />
                </>
              )} />
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

// ------------------------------------------------------------------ queues and health

// How many are in a list, next to its title.
function CountBadge({ value, tone = null }) {
  if (value === null || value === undefined) return null;
  return <span className={`xo-dash-count${tone ? ` is-${tone}` : ''}`}>{NUMBER.format(value)}</span>;
}

function QueueValidations({ permissions, height }) {
  const global = scoped(permissions).validationsGlobal;
  const load = useMemo(() => validationsReader(global), [global]);
  const resource = useWidgetData(['validations'], `validations-${global}`, load);
  const total = resource.data ? resource.data.local.total : null;
  return (
    <WidgetCard title={title('queue_validations')} detail={w('queue_validations', 'detail')} to="/xomics/validation"
      aside={<CountBadge value={total} tone={total > 0 ? 'attention' : null} />}>
      <Loaded resource={resource}>
        {(data) => (data.local.validations.length === 0
          ? <EmptyNote icon={BadgeCheck} title={t('validations.clear')} detail={t('validations.clear_detail')} />
          : (
            <RowList>
              {data.local.validations.slice(0, rowsFor(height, LIST)).map((item) => (
                <Row key={item.collection.uuid} to="/xomics/validation" dot="warn"
                  title={item.patient.name || item.latestJob.pipelineName}
                  detail={[item.patient.name ? item.latestJob.pipelineName : null, item.project ? item.project.name : null].filter(Boolean).join(' · ')}
                  meta={ago(item.createdAt)} />
              ))}
            </RowList>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

function QueueOrders({ permissions }) {
  const scope = scoped(permissions).orders;
  const load = useMemo(() => ordersReader(scope), [scope]);
  const resource = useWidgetData(['orders', 'shipments'], `orders-${scope}`, load);
  const total = resource.data ? resource.data.unsentTotal : null;
  return (
    <WidgetCard title={title('queue_orders')} detail={w('queue_orders', 'detail')} to="/xomics/orders"
      aside={<CountBadge value={total} tone={total > 0 ? 'attention' : null} />}>
      <Loaded resource={resource}>
        {(data) => (data.unsent.length === 0
          ? <EmptyNote icon={PackageCheck} title={t('orders.clear')} detail={t('orders.clear_detail')} />
          : (
            <RowList>
              {data.unsent.map((order) => (
                <Row key={order.uuid} to={`/xomics/orders?orderUuid=${encodeURIComponent(order.uuid)}${scope === 'global' ? '&scope=global' : ''}`}
                  title={String(order.trackingCode || '').replace(/(.{4})(?=.)/g, '$1 ')}
                  lead={<Flag code={order.address.countryCode} />}
                  detail={[order.address.city, countryName(order.address.countryCode)].filter(Boolean).join(', ')}
                  meta={ago(order.placedAt)} />
              ))}
            </RowList>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

const SHIPMENT_STAGES = [['label_printed', 'soft'], ['dispatched', 'mid'], ['in_transit', 'navy'], ['delivered', 'good']];
const SHIPMENT_TONES = { label_printed: 'flat', dispatched: 'info', in_transit: 'info', delivered: 'good' };

function QueueShipments({ permissions, height }) {
  const scope = scoped(permissions).shipments;
  const load = useMemo(() => shipmentsReader(scope), [scope]);
  const resource = useWidgetData(['shipments'], `shipments-${scope}`, load);
  return (
    <WidgetCard title={title('queue_shipments')} detail={w('queue_shipments', 'detail')} to="/xomics/shipments">
      <Loaded resource={resource}>
        {(data) => (
          <>
            <StageBar to="/xomics/shipments" compact stages={SHIPMENT_STAGES.map(([key, tone]) => ({
              key, tone, label: tx(`ui.values.shipment_${key}`), value: data.summary.byStatus[key] || 0,
            }))} />
            {data.shipments.length === 0
              ? <EmptyNote icon={Truck} title={t('shipments.clear')} detail={t('shipments.clear_detail')} />
              : (
                <RowList>
                  {data.shipments.slice(0, rowsFor(height, WITH_BAR)).map((shipment) => (
                    <Row key={shipment.uuid} to="/xomics/shipments"
                      title={shipment.subject.printedReference || shipment.subject.reference || shipment.trackingNumber}
                      detail={`${shipment.fromLocation.address.city || shipment.fromLocation.name} → ${shipment.destination.address.city || shipment.destination.name}`}
                      pill={<Tag tone={SHIPMENT_TONES[shipment.status] || 'flat'}>{tx(`ui.values.shipment_${shipment.status}`)}</Tag>} />
                  ))}
                </RowList>
              )}
          </>
        )}
      </Loaded>
    </WidgetCard>
  );
}

const LAB_STAGES = [['awaiting_acceptance', 'mid'], ['on_hold', 'warn'], ['quarantined', 'bad'], ['ready', 'navy'], ['assigned', 'good']];

function QueueLabSamples({ height }) {
  const resource = useWidgetData(['samples', 'laboratory'], 'lab-samples', readLabSamples);
  return (
    <WidgetCard title={title('queue_lab_samples')} detail={w('queue_lab_samples', 'detail')} to="/xomics/laboratory/samples">
      <Loaded resource={resource}>
        {atLab((data) => (
          <>
            <StageBar to="/xomics/laboratory/samples" compact stages={LAB_STAGES.map(([key, tone]) => ({
              key, tone, label: tx(`ui.lab.inventory.tab.${key}`), value: data.statusCounts[key] || 0,
            }))} />
            {data.samples.length === 0
              ? <EmptyNote icon={FlaskConical} title={t('lab.empty')} detail={t('lab.empty_detail')} />
              : (
                <RowList>
                  {data.samples.slice(0, rowsFor(height, WITH_BAR)).map((sample) => (
                    <Row key={sample.collectionUuid} to="/xomics/laboratory/samples" title={sample.printedCode ?? sample.code}
                      detail={kitName(sample)} meta={ago(sample.lastReceivedAt)} />
                  ))}
                </RowList>
              )}
          </>
        ))}
      </Loaded>
    </WidgetCard>
  );
}

const WORK_STAGES = [['planned', 'soft'], ['in_progress', 'navy'], ['awaiting_approval', 'warn'], ['awaiting_customer', 'warn'], ['completed', 'good'],
  ['stopped', 'bad']];

function QueueLabWork() {
  const work = useWidgetData(['samples', 'laboratory'], 'lab-work', readLabWork);
  const approvals = useWidgetData(['samples', 'laboratory'], 'lab-approvals', readLabApprovals);
  return (
    <WidgetCard title={title('queue_lab_work')} detail={w('queue_lab_work', 'detail')} to="/xomics/laboratory/work">
      <Loaded resource={work}>
        {atLab((data) => {
          const waiting = approvals.data && !approvals.data.notLaboratory ? approvals.data.stageRecords.length + approvals.data.acceptances.length : null;
          const counts = data.statusCounts;
          const all = counts.all || 0;
          return (
            <>
              <div className="xo-dash-health">
                <Ring value={counts.completed || 0} total={all} size={96} stroke={8} tone="good"
                  label={all > 0 ? PERCENT.format((counts.completed || 0) / all) : t('none')} caption={tx('ui.lab.stage.order.completed')} />
                <div className="xo-dash-health-facts">
                  <Facts items={[
                    { label: t('kpi_lab.open_work'), value: (counts.planned || 0) + (counts.in_progress || 0) },
                    { label: tx('ui.lab.stage.order.stopped'), value: counts.stopped || 0, tone: counts.stopped > 0 ? 'bad' : null },
                  ]} />
                </div>
              </div>
              <StageBar to="/xomics/laboratory/work" compact stages={WORK_STAGES.map(([key, tone]) => ({
                key, tone, label: tx(`ui.lab.stage.order.${key}`), value: counts[key] || 0,
              }))} />
              <Link className={`xo-dash-callout${waiting ? ' is-attention' : ''}`} to="/xomics/laboratory/approvals">
                <ShieldCheck aria-hidden="true" />
                <span><strong>{waiting === null ? '…' : NUMBER.format(waiting)}</strong>{t('lab.approvals_waiting')}</span>
              </Link>
            </>
          );
        })}
      </Loaded>
    </WidgetCard>
  );
}

const REPORT_STAGES = [['queued', 'soft'], ['processing', 'navy'], ['done', 'good'], ['failed', 'bad']];
const REPORT_TONES = { queued: 'flat', processing: 'info', done: 'good', failed: 'bad' };
// A finished report is 'done' in the data and "Ready" in the Reporting section's words.
const reportStatus = (status) => known('ui.reports.status_', status === 'done' ? 'ready' : status);

function QueueReports({ height }) {
  const resource = useWidgetData(['reports'], 'report-queue', readReportQueue, { gapMs: AUDITED_GAP_MS });
  return (
    <WidgetCard title={title('queue_reports')} detail={w('queue_reports', 'detail')} to="/xomics/reporting">
      <Loaded resource={resource}>
        {(data) => (
          <>
            <Tally items={REPORT_STAGES.map(([key, tone]) => ({
              key, tone, label: reportStatus(key), value: data.statusCounts[key] || 0, to: `/xomics/reporting?status=${key}`,
            }))} />
            {data.requests.length === 0
              ? <EmptyNote icon={FileText} title={t('reports.clear')} detail={t('reports.clear_detail')} />
              : (
                <RowList>
                  {data.requests.slice(0, rowsFor(height, WITH_TALLY)).map((request) => (
                    <Row key={request.uuid} to="/xomics/reporting" title={request.reportType.displayName}
                      detail={`${request.company.name} · ${ago(request.requestedAt)}`}
                      pill={<Tag tone={REPORT_TONES[request.status] || 'flat'}>{reportStatus(request.status)}</Tag>} />
                  ))}
                </RowList>
              )}
          </>
        )}
      </Loaded>
    </WidgetCard>
  );
}

const JOB_TONES = { pending: 'flat', running: 'info', offloading: 'info', complete: 'good', failed: 'bad', cancelled: 'flat' };
const JOB_STATES = { pending: 'queued', running: 'running', offloading: 'securing_outputs', complete: 'complete', failed: 'failed', cancelled: 'cancelled' };

function QueueUploads({ height }) {
  const resource = useWidgetData(['jobs', 'uploads'], 'jobs', readJobs);
  return (
    <WidgetCard title={title('queue_uploads')} detail={w('queue_uploads', 'detail')} to="/xomics/uploads">
      <Loaded resource={resource}>
        {(data) => (
          <>
            <Tally to="/xomics/uploads" items={[
              { key: 'queued', tone: 'soft', label: tx('ui.uploads.filter_queued'), value: data.counts.queued },
              { key: 'running', tone: 'navy', label: tx('ui.uploads.filter_running'), value: data.counts.running },
              { key: 'completed', tone: 'good', label: tx('ui.uploads.filter_completed'), value: data.counts.completed },
              { key: 'failed', tone: 'bad', label: tx('ui.uploads.filter_failed'), value: data.counts.failed },
            ]} />
            {data.jobs.length === 0
              ? <EmptyNote icon={Upload} title={t('uploads.empty')} detail={t('uploads.empty_detail')} />
              : (
                <RowList>
                  {data.jobs.slice(0, rowsFor(height, WITH_TALLY)).map((job) => (
                    <Row key={job.uuid} to="/xomics/uploads" title={job.display_name || job.pipeline}
                      detail={[job.sample_name || job.sample_code, job.code_version ? `v${job.code_version}` : null, ago(job.updated_at)].filter(Boolean).join(' · ')}
                      pill={<Tag tone={JOB_TONES[job.state] || 'flat'}>{tx(`ui.dashboard.states.${JOB_STATES[job.state] || 'unknown'}`)}</Tag>} />
                  ))}
                </RowList>
              )}
          </>
        )}
      </Loaded>
    </WidgetCard>
  );
}

// Customers waiting for their samples' data: each opens Uploads with its code
// filled in, and leaves the list once an upload of it exists.
function QueueRequestedUploads({ height }) {
  const resource = useWidgetData(['uploads', 'notifications'], 'requested_uploads', readRequestedUploads);
  const count = resource.data ? resource.data.length : null;
  return (
    <WidgetCard title={title('queue_requested_uploads')} detail={w('queue_requested_uploads', 'detail')} to="/xomics/uploads"
      aside={count > 0 ? <CountBadge value={count} tone="attention" /> : null}>
      <Loaded resource={resource}>
        {(requested) => (requested.length === 0
          ? <EmptyNote icon={Upload} title={t('requested.empty')} detail={t('requested.empty_detail')} />
          : (
            <RowList>
              {requested.slice(0, rowsFor(height, LIST)).map((item) => (
                <Row key={item.code} to={`/xomics/uploads?code=${encodeURIComponent(item.code)}`}
                  title={item.person || item.sampleName || item.code}
                  detail={[item.code, t('requested.where', item)].join(' · ')} meta={ago(item.askedAt)} />
              ))}
            </RowList>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

function HealthCluster() {
  const resource = useWidgetData(['cluster', 'jobs', 'pipelines'], 'overview', readOverview);
  return (
    <WidgetCard title={title('health_cluster')} detail={w('health_cluster', 'detail')} to="/xomics/cluster">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          if (data.cluster === null) return <EmptyNote icon={Server} title={t('cluster.unavailable')} />;
          const { nodes } = data.cluster;
          const counts = data.analysis ? data.analysis.counts : null;
          const healthy = nodes.total > 0 && nodes.unreachable === 0;
          return (
            <>
              <div className="xo-dash-health">
                <Ring value={nodes.online} total={nodes.total} tone={healthy ? 'good' : nodes.online === 0 ? 'bad' : 'warn'}
                  label={`${NUMBER.format(nodes.online)}/${NUMBER.format(nodes.total)}`} caption={t('cluster.workers_online')} />
                <div className="xo-dash-health-facts">
                  <Facts items={[
                    { label: t('cluster.pipelines'), value: data.cluster.pipelinesOnline },
                    { label: t('cluster.unreachable'), value: nodes.unreachable, tone: nodes.unreachable > 0 ? 'bad' : null },
                  ]} />
                  {data.cluster.release && (
                    <p className="xo-dash-release"><Zap aria-hidden="true" />{t('cluster.release', { tag: data.cluster.release.tag })}<small>{ago(data.cluster.release.createdAt)}</small></p>
                  )}
                </div>
              </div>
              {counts && (
                <div className="xo-dash-health-flow">
                  <small>{t('cluster.flow')}</small>
                  <StageBar to="/xomics/uploads" compact stages={[
                    { key: 'queued', tone: 'soft', label: tx('ui.dashboard.states.queued'), value: counts.queued },
                    { key: 'running', tone: 'navy', label: tx('ui.dashboard.states.running'), value: counts.running },
                    { key: 'offloading', tone: 'mid', label: tx('ui.dashboard.states.securing_outputs'), value: counts.offloading },
                  ]} />
                </div>
              )}
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function HealthPipelines() {
  const resource = useWidgetData(['pipelines'], 'pipelines', readPipelines);
  return (
    <WidgetCard title={title('health_pipelines')} to="/xomics/pipelines"
      detail={resource.data && resource.data.activeRelease ? t('cluster.release', { tag: resource.data.activeRelease.tag }) : w('health_pipelines', 'detail')}>
      <Loaded resource={resource}>
        {(data) => (data.pipelines.length === 0
          ? <EmptyNote icon={Workflow} title={t('pipelines.empty')} />
          : (
            <ol className="xo-dash-pipelines">
              {data.pipelines.slice(0, 6).map((pipeline) => {
                const runs = pipeline.runs || { total: 0, complete: 0, failed: 0 };
                const online = pipeline.status === 'online';
                return (
                  <li key={pipeline.id}>
                    <i className={`xo-dash-status-dot${online ? ' is-on' : ''}`} title={t(online ? 'pipelines.online' : 'pipelines.offline')} />
                    <span><strong>{pipeline.displayName}</strong><small>{[pipeline.omics, pipeline.codeVersion ? `v${pipeline.codeVersion}` : null].filter(Boolean).join(' · ')}</small></span>
                    <span className="xo-dash-pipeline-runs" title={t('pipelines.runs', { complete: runs.complete, failed: runs.failed })}>
                      <ShareBar value={runs.complete} total={Math.max(1, runs.total)} tone="good" />
                      <em>{NUMBER.format(runs.total)}</em>
                    </span>
                  </li>
                );
              })}
            </ol>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

function StoreFunnel() {
  const resource = useWidgetData(['store_traffic', 'store'], 'store-month', readStoreMonth, { gapMs: MINUTE });
  return (
    <WidgetCard title={title('store_funnel')} detail={w('store_funnel', 'detail')} to="/xomics/store/checkout-funnel">
      <Loaded resource={resource} skeleton={<SkeletonList rows={5} />}>
        {(data) => {
          if (data.report === null) return <EmptyNote icon={MousePointerClick} title={t('store.no_site')} detail={t('store.no_site_detail')} />;
          const steps = data.report.funnel;
          const first = steps.length > 0 ? steps[0].sessions : 0;
          return (
            <ol className="xo-dash-funnel">
              {steps.map((step, index) => (
                <li key={step.stage}>
                  <span>{storeText('stage', step.stage)}</span>
                  <span className="xo-dash-funnel-bar"><ShareBar value={step.sessions} total={first} tone={index === steps.length - 1 ? 'good' : 'navy'} /></span>
                  <strong>{NUMBER.format(step.sessions)}</strong>
                  <small>{first > 0 ? PERCENT.format(step.sessions / first) : t('none')}</small>
                </li>
              ))}
            </ol>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function StoreCampaigns() {
  const resource = useWidgetData(['store'], 'store-campaigns', readStoreCampaigns, { gapMs: MINUTE });
  return (
    <WidgetCard title={title('store_campaigns')} detail={w('store_campaigns', 'detail')} to="/xomics/store/campaigns">
      <Loaded resource={resource}>
        {(data) => {
          const campaigns = data.campaigns ? data.campaigns.campaigns.filter((campaign) => campaign.performance.sessions > 0).slice(0, 5) : [];
          if (campaigns.length === 0) return <EmptyNote icon={Megaphone} title={t('campaigns.empty')} detail={t('campaigns.empty_detail')} />;
          const most = Math.max(1, ...campaigns.map((campaign) => campaign.performance.sessions));
          return (
            <ol className="xo-dash-rank">
              {campaigns.map((campaign) => (
                <li key={campaign.id}>
                  <span><strong>{campaign.name}</strong><small>{storeText('channel', campaign.channel)} · {counted('kpi_store.orders', campaign.performance.orders)}</small></span>
                  <ShareBar value={campaign.performance.sessions} total={most} />
                  <em>{COMPACT.format(campaign.performance.sessions)}</em>
                </li>
              ))}
            </ol>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

const STEP_ICONS = { created: Package, printed: ClipboardList, activation: BadgeCheck, transit: Truck, custody: PackageCheck, output: FileText };

function CollectionLifecycle({ permissions }) {
  const scope = scoped(permissions).kits;
  const load = useMemo(() => activityReader(scope), [scope]);
  const resource = useWidgetData(['collections'], `activity-${scope}`, load, { reconcileMs: MINUTE });
  return (
    <WidgetCard title={title('collection_lifecycle')} detail={w('collection_lifecycle', 'detail')} to="/xomics/kits">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          const { lifecycle } = data;
          if (lifecycle.total === 0) return <EmptyNote icon={Boxes} title={t('lifecycle.empty')} detail={t('lifecycle.empty_detail')} />;
          const steps = lifecycle.steps.filter((step) => step.source === 'collection' || step.isActive || step.reached > 0);
          return (
            <ol className="xo-dash-lifecycle" style={{ '--steps': steps.length }}>
              {steps.map((step) => {
                const Icon = STEP_ICONS[step.code] || STEP_ICONS[step.kind] || Sparkles;
                return (
                  <li key={step.code} className={step.reached > 0 ? 'is-reached' : ''}>
                    <span className="xo-dash-lifecycle-mark" aria-hidden="true"><Icon /></span>
                    <span className="xo-dash-lifecycle-name">{stepLabel(step.code)}</span>
                    <strong><Counter value={step.reached} /></strong>
                    <ShareBar value={step.reached} total={lifecycle.total} />
                    <small>{counted('lifecycle.now', step.current)}</small>
                  </li>
                );
              })}
            </ol>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

// ------------------------------------------------------------------ feeds and shortcuts

function AuditActivity({ height }) {
  const resource = useWidgetData(['audit'], 'audit', readAudit, { gapMs: MINUTE });
  return (
    <WidgetCard title={title('audit_activity')} detail={w('audit_activity', 'detail')} to="/xomics/audit">
      <Loaded resource={resource} skeleton={<SkeletonChart />}>
        {(data) => {
          const most = Math.max(1, ...data.series.map((point) => point.events));
          return (
            <>
              <Headline value={data.totals.events} caption={t('audit.events')} side={[
                { label: t('audit.people'), value: data.totals.people },
                { label: t('audit.refused'), value: data.totals.failed, tone: data.totals.failed > 0 ? 'bad' : null },
              ]} />
              <div className="xo-dash-bars" aria-hidden="true">
                {data.series.slice(-24).map((point) => (
                  <i key={point.at} style={{ height: `${Math.max(point.events > 0 ? 6 : 2, (point.events / most) * 100)}%` }}
                    className={point.failed > 0 ? 'has-failed' : ''} title={`${new Date(point.at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} · ${point.events}`} />
                ))}
              </div>
              <ol className="xo-dash-rank">
                {data.actions.slice(0, Math.max(0, Math.floor((tilePixels(height) - AUDIT_ABOVE) / AUDIT_ROW))).map((action) => (
                  <li key={action.action_code} className="is-plain"><span><strong>{auditActionLabel(action.action_code, action.action_label)}</strong></span><em>{NUMBER.format(action.events)}</em></li>
                ))}
              </ol>
            </>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

function FeedNotifications({ height }) {
  const resource = useWidgetData(['notifications'], 'notifications', readNotifications, { gapMs: 1500 });
  const unread = resource.data ? resource.data.unread : null;
  return (
    <WidgetCard title={title('feed_notifications')} to="/xomics/notifications"
      detail={resource.data ? counted('notifications.unread', resource.data.unread) : null}
      aside={unread > 0 ? <CountBadge value={unread} tone="info" /> : null}>
      <Loaded resource={resource}>
        {(data) => (data.notifications.length === 0
          ? <EmptyNote icon={BellRing} title={t('notifications.empty')} detail={t('notifications.empty_detail')} />
          : (
            <RowList>
              {data.notifications.slice(0, rowsFor(height, LIST)).map((item) => (
                <Row key={item.id} to={item.actionRoute || '/xomics/notifications'} dot={item.isRead ? 'read' : 'info'}
                  title={notificationEventTitle(item.type)} detail={notificationEventMessage(item.type, item.messageVariables || {})} meta={ago(item.createdAt)} />
              ))}
            </RowList>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

function FeedActivations({ permissions, height }) {
  const scope = scoped(permissions).kits;
  const load = useMemo(() => activationsReader(scope), [scope]);
  const resource = useWidgetData(['collections'], `activations-${scope}`, load, { reconcileMs: MINUTE });
  return (
    <WidgetCard title={title('feed_activations')} detail={w('feed_activations', 'detail')} to="/xomics/kits">
      <Loaded resource={resource}>
        {(data) => (data.activations.length === 0
          ? <EmptyNote icon={Package} title={t('activations.empty')} detail={t('activations.empty_detail')} />
          : (
            <RowList>
              {data.activations.slice(0, rowsFor(height, LIST)).map((collection) => {
                const place = placeLabel(collection.activation.place);
                return (
                  <Row key={collection.uuid} to="/xomics/kits"
                    lead={<span className="xo-dash-row-glyph" aria-hidden="true">{collection.activation.channel === 'portal' ? <Monitor /> : <Smartphone />}</span>}
                    title={activatorName(collection.activation)}
                    detail={[collection.printedCode ?? collection.code, kitName(collection), place].filter(Boolean).join(' · ')} meta={ago(collection.activation.at)} />
                );
              })}
            </RowList>
          ))}
      </Loaded>
    </WidgetCard>
  );
}

const EVENT_ICONS = { purchase: ShoppingBag, bag_add: ShoppingCart, bag_remove: ShoppingCart, checkout_step: ClipboardList, payment_started: CircleDollarSign,
  payment_failed: CircleDollarSign, coupon_attempt: Sparkles, error: Zap, page_view: MousePointerClick };
const EVENT_TONES = { purchase: 'good', payment_failed: 'bad', error: 'bad', bag_add: 'info', checkout_step: 'info', payment_started: 'info' };

function FeedStore({ height }) {
  const resource = useWidgetData(['store_live'], 'store-live', readStoreLive, { gapMs: 5000, reconcileMs: 30000 });
  return (
    <WidgetCard title={title('feed_store')} detail={w('feed_store', 'detail')} to="/xomics/store/live">
      <Loaded resource={resource}>
        {(data) => {
          const feed = data.live ? data.live.feed.filter((event) => event.type !== 'page_view').slice(0, rowsFor(height, LIST)) : [];
          if (feed.length === 0) return <EmptyNote icon={Zap} title={t('store.quiet')} detail={t('store.quiet_detail')} />;
          return (
            <RowList>
              {feed.map((event) => {
                const Icon = EVENT_ICONS[event.type] || Sparkles;
                return (
                  <Row key={`${event.sessionId}-${event.at}-${event.type}`} to="/xomics/store/live"
                    lead={<span className={`xo-dash-row-glyph is-${EVENT_TONES[event.type] || 'flat'}`} aria-hidden="true"><Icon /></span>}
                    title={storeText('event_type', event.type)} detail={event.subject || event.productCode || event.path} meta={ago(event.at)} />
                );
              })}
            </RowList>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

// The places this person is most likely to go next, from the ones they may open.
const ACTIONS = [
  ['validation', BadgeCheck], ['laboratory/receipt', ScanLine], ['laboratory/samples', FlaskConical], ['laboratory/work', Workflow],
  ['laboratory/approvals', ShieldCheck], ['reporting/requests', FileText], ['reporting/files', FolderOpen], ['uploads', Upload],
  ['pipelines/catalogue', Workflow], ['orders', ShoppingCart], ['shipments', Truck], ['commerce/overview', CircleDollarSign], ['products', Boxes],
  ['kits', Package], ['store/live', Radio], ['store/overview', TrendingUp], ['store/campaigns', Megaphone], ['store/sessions', MousePointerClick],
  ['mobile/overview', Smartphone], ['messaging', BellRing], ['projects', LayoutGrid], ['reporting/analytics', Activity], ['cluster', Server],
  ['staff', Users], ['audit', History],
  // A warehouse's and a shop's own pages, and an office's requests to a warehouse.
  ['warehouse/requests', Inbox], ['warehouse/stock', Warehouse], ['retail/stock', ShoppingBag], ['store/stock-requests', PackagePlus],
];

function ShortcutsActions({ permissions, width }) {
  // Where the account works decides some of its pages (a warehouse's, a shop's).
  const kind = useXomicsAuth().user?.location?.kind ?? null;
  const destinations = useMemo(() => new Map(destinationsOf(visibleNav(permissions, kind)).filter((item) => item.built).map((item) => [item.path, item])), [kind, permissions]);
  const shown = ACTIONS.filter(([path]) => destinations.has(path)).slice(0, width >= 2 ? 8 : 4);
  return (
    <WidgetCard title={title('shortcuts_actions')} detail={w('shortcuts_actions', 'detail')}>
      {shown.length === 0 ? <EmptyNote icon={Sparkles} title={t('shortcuts.none')} /> : (
        <nav className="xo-dash-shortcuts" aria-label={title('shortcuts_actions')}>
          {shown.map(([path, Icon]) => {
            const item = destinations.get(path);
            return (
              <Link key={path} to={`/xomics/${path}`}>
                <span aria-hidden="true"><Icon /></span>
                <strong>{item.label}<small>{item.parent || item.section}</small></strong>
              </Link>
            );
          })}
        </nav>
      )}
    </WidgetCard>
  );
}

function ShortcutsRecents({ permissions, height }) {
  const resource = useWidgetData([], 'recents', readRecents);
  const labels = useMemo(() => new Map(destinationsOf(visibleNav(permissions)).map((item) => [`/xomics/${item.path}`, item])), [permissions]);
  return (
    <WidgetCard title={title('shortcuts_recents')} detail={w('shortcuts_recents', 'detail')}>
      <Loaded resource={resource}>
        {(recents) => {
          // Only screens this person can open now: a permission taken away takes its shortcut with it.
          const rows = recents.filter((recent) => recent.path !== '/xomics/overview' && labels.has(recent.path)).slice(0, rowsFor(height, LIST));
          if (rows.length === 0) return <EmptyNote icon={Clock} title={t('recents.empty')} detail={t('recents.empty_detail')} />;
          return (
            <RowList>
              {rows.map((recent) => {
                const item = labels.get(recent.path);
                const Icon = item.icon || Clock;
                return <Row key={recent.path} lead={<span className="xo-dash-row-glyph" aria-hidden="true"><Icon /></span>} to={recent.path}
                  state={{ viaRecents: true }} title={item.label} detail={item.parent || item.section} meta={ago(recent.at)} />;
              })}
            </RowList>
          );
        }}
      </Loaded>
    </WidgetCard>
  );
}

// ------------------------------------------------------------------ the registry

// group: where the library lists it. The permission each needs, and how wide it
// may be, come from the database's catalogue, not from here.
export const WIDGETS = {
  kpi_revenue: { group: 'numbers', icon: CircleDollarSign, Component: KpiRevenue },
  kpi_unsent_orders: { group: 'numbers', icon: ShoppingCart, Component: KpiUnsentOrders },
  kpi_activations: { group: 'numbers', icon: Package, Component: KpiActivations },
  kpi_validations: { group: 'numbers', icon: BadgeCheck, Component: KpiValidations },
  kpi_lab_queue: { group: 'numbers', icon: FlaskConical, Component: KpiLabQueue },
  kpi_lab_work: { group: 'numbers', icon: Workflow, Component: KpiLabWork },
  kpi_reports: { group: 'numbers', icon: FileText, Component: KpiReports },
  kpi_analyses: { group: 'numbers', icon: Activity, Component: KpiAnalyses },
  kpi_store_visitors: { group: 'numbers', icon: Users, Component: KpiStoreVisitors },
  kpi_store_conversion: { group: 'numbers', icon: TrendingUp, Component: KpiStoreConversion },
  kpi_store_revenue: { group: 'numbers', icon: ShoppingBag, Component: KpiStoreRevenue },
  kpi_app_people: { group: 'numbers', icon: Smartphone, Component: KpiAppPeople },
  chart_revenue: { group: 'charts', icon: CircleDollarSign, Component: ChartRevenue },
  chart_store_traffic: { group: 'charts', icon: Globe2, Component: ChartStoreTraffic },
  chart_analyses: { group: 'charts', icon: Activity, Component: ChartAnalyses },
  chart_activations: { group: 'charts', icon: Package, Component: ChartActivations },
  chart_reports: { group: 'charts', icon: FileText, Component: ChartReports },
  chart_lab_intake: { group: 'charts', icon: ScanLine, Component: ChartLabIntake },
  map_store_live: { group: 'maps', icon: Radio, Component: MapStoreLive },
  map_activations: { group: 'maps', icon: MapPin, Component: MapActivations },
  map_app: { group: 'maps', icon: Smartphone, Component: MapApp },
  map_orders: { group: 'maps', icon: Globe2, Component: MapOrders },
  queue_validations: { group: 'work', icon: BadgeCheck, Component: QueueValidations },
  queue_orders: { group: 'work', icon: ShoppingCart, Component: QueueOrders },
  queue_shipments: { group: 'work', icon: Truck, Component: QueueShipments },
  queue_lab_samples: { group: 'work', icon: FlaskConical, Component: QueueLabSamples },
  queue_lab_work: { group: 'work', icon: Workflow, Component: QueueLabWork },
  queue_reports: { group: 'work', icon: ClipboardList, Component: QueueReports },
  queue_uploads: { group: 'work', icon: Upload, Component: QueueUploads },
  queue_requested_uploads: { group: 'work', icon: Inbox, Component: QueueRequestedUploads },
  health_cluster: { group: 'work', icon: Server, Component: HealthCluster },
  health_pipelines: { group: 'work', icon: Workflow, Component: HealthPipelines },
  store_funnel: { group: 'store', icon: MousePointerClick, Component: StoreFunnel },
  store_campaigns: { group: 'store', icon: Megaphone, Component: StoreCampaigns },
  collection_lifecycle: { group: 'work', icon: Boxes, Component: CollectionLifecycle },
  audit_activity: { group: 'feeds', icon: History, Component: AuditActivity },
  feed_notifications: { group: 'feeds', icon: BellRing, Component: FeedNotifications },
  feed_activations: { group: 'feeds', icon: BadgeCheck, Component: FeedActivations },
  feed_store: { group: 'feeds', icon: Zap, Component: FeedStore },
  shortcuts_actions: { group: 'feeds', icon: Sparkles, Component: ShortcutsActions },
  shortcuts_recents: { group: 'feeds', icon: Clock, Component: ShortcutsRecents },
};

export const GROUPS = ['numbers', 'charts', 'maps', 'work', 'store', 'feeds'];

export function widgetTitle(code) {
  return title(code);
}

export function widgetAbout(code) {
  return w(code, 'about');
}
